/**
 * @hon/core — the visual-novel runtime.
 *
 * Deliberately headless: it walks a pack's node graph and emits typed events.
 * Phaser renders the stage, React renders the UI, and the editor drives this same
 * class for live preview. Nothing here imports a renderer.
 */
import type { Book, ChoiceRecord, Episode, Node, PackManifest, SaveState } from '@hon/schema';
import { findEpisode, firstEpisode, initialValue } from '@hon/schema';
import { evaluate, type Scope } from './expr.js';

export type RuntimeEvent =
  | {
      type: 'say'; who: string | undefined; name: string | undefined; color: string; text: string;
      emote?: string; voice?: string;
      /** Which of four heights the dialogue frame sits at for this line. */
      at?: 'upper' | 'middle' | 'lower' | 'bottom';
    }
  | {
      type: 'show'; sprite: string; at: string; emote?: string;
      /** Exact placement off the storyboard, as percentages of the board. */
      frame?: { x: number; y: number; w: number; h: number };
      transition: { kind: string; ms: number };
    }
  | { type: 'hide'; sprite: string; transition: { kind: string; ms: number } }
  | {
      type: 'bg'; image: string;
      /** Where the camera sits on the image, as board percentages. */
      frame?: { x: number; y: number; w: number; h: number };
      transition: { kind: string; ms: number };
    }
  | { type: 'bgm'; track: string | null; fadeMs: number }
  | { type: 'sfx'; clip: string; volume: number }
  | { type: 'fx'; effect: string; ms: number; intensity: number }
  | { type: 'wait'; ms: number }
  | {
      type: 'choice';
      prompt?: string;
      /** Pack-relative art for the prompt card, where the choice has one. */
      promptImage?: string;
      /** `premium`/`diamonds` are presentational only — nothing is gated yet. */
      options: Array<{ text: string; goto: string; index: number; premium: boolean; diamonds: number }>;
    }
  /** The player is being asked for a name. Resolve with `submit()`. */
  | { type: 'input'; key: string; prompt: string; note?: string; value: string; maxLength: number }
  /** An interstitial the reader taps past — «ТРИ ДНЯ НАЗАД». */
  | { type: 'card'; title?: string; subtitle?: string }
  | { type: 'scene'; id: string }
  /** The episode's graph ran out. `next` is the following episode in the book, if any. */
  | { type: 'end'; book: string; episode: string; next: { book: string; episode: string } | undefined };

type Listener = (e: RuntimeEvent) => void;

const DEFAULT_TRANSITION = { kind: 'fade', ms: 300 };

/** Ceiling on the recap log, matching `SaveState.choices.max(400)`. */
const MAX_CHOICE_RECORDS = 400;

export interface HistoryEntry {
  who: string | undefined;
  name: string | undefined;
  text: string;
}

export class Runtime {
  private frames: Array<{ nodes: Node[]; cursor: number }> = [];
  private listeners = new Set<Listener>();
  private startedAt = 0;

  readonly history: HistoryEntry[] = [];
  /** Every choice taken this playthrough, for a "choices that mattered" recap. */
  choices: ChoiceRecord[] = [];
  vars: Record<string, string | number | boolean> = {};
  seen = new Set<string>();
  completed = new Set<string>();
  sceneId: string;
  book: Book;
  episode: Episode;
  /** Blocks `advance()` until the UI reports a choice. Prevents click-through. */
  awaitingChoice = false;
  /**
   * The pending `input`, if the story is waiting on one.
   *
   * Separate from `awaitingChoice` because the two resolve differently — a
   * choice takes an index and jumps, an input takes text and carries on — and
   * because a save taken mid-question has to know which of the two it is.
   */
  awaitingInput: { key: string; fallback: string } | null = null;

  /**
   * Wall clock for choice timestamps.
   *
   * `save()` takes `now` as an argument because the host always has one to
   * hand there. `choose()` is driven by a click, where threading a clock
   * through the UI would be noise — so it reads this instead. Injectable so
   * tests stay deterministic.
   */
  private readonly clock: () => number;

  constructor(
    private readonly pack: PackManifest,
    opts?: { now?: number; book?: string; episode?: string; clock?: () => number },
  ) {
    const target = opts?.book && opts.episode
      ? findEpisode(pack, opts.book, opts.episode)
      : firstEpisode(pack);
    if (!target) throw new Error(`[hon] no episode ${opts?.book}/${opts?.episode} in pack ${pack.id}`);

    this.book = target.book;
    this.episode = target.episode;
    this.sceneId = target.episode.entry;
    this.startedAt = opts?.now ?? 0;
    this.clock = opts?.clock ?? (() => Date.now());
    this.seedVars();
    this.loadScene(target.episode.entry);
  }

  /** Swap to another episode, keeping vars and completion history. */
  playEpisode(bookId: string, episodeId: string): void {
    const target = findEpisode(this.pack, bookId, episodeId);
    if (!target) throw new Error(`[hon] no episode ${bookId}/${episodeId} in pack ${this.pack.id}`);
    this.book = target.book;
    this.episode = target.episode;
    this.awaitingChoice = false;
    this.awaitingInput = null;
    this.history.length = 0;
    this.loadScene(target.episode.entry);
  }

  /** The next episode in the current book, if there is one. */
  private nextEpisode(): { book: string; episode: string } | undefined {
    const i = this.book.episodes.findIndex((e) => e.id === this.episode.id);
    const next = i >= 0 ? this.book.episodes[i + 1] : undefined;
    return next ? { book: this.book.id, episode: next.id } : undefined;
  }

  private endEvent(): RuntimeEvent {
    this.completed.add(this.episode.id);
    return { type: 'end', book: this.book.id, episode: this.episode.id, next: this.nextEpisode() };
  }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: RuntimeEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  private get scope(): Scope {
    return { vars: this.vars, ...this.vars };
  }

  /**
   * Give every declared variable a value, without disturbing one the story has
   * already set.
   *
   * Run at construction and again after a restore. The restore case is the one
   * that matters: a variable added in a later episode is simply absent from an
   * existing save, and an absent name is `undefined`, which compares false
   * against everything. That is how a returning player's condition silently
   * disagrees with a new player's. Filling from the declaration closes it
   * centrally, rather than relying on every episode's entry scene remembering a
   * defaulting guard.
   */
  private seedVars(): void {
    for (const decl of this.pack.vars) {
      if (this.vars[decl.name] === undefined) this.vars[decl.name] = initialValue(decl);
    }
  }

  /**
   * Apply a relative change, pinned inside the variable's declared domain.
   *
   * Clamping here rather than in content is the whole point: a stat that can be
   * walked past its ceiling makes every threshold downstream unreliable, and no
   * author should have to write a guard for it.
   */
  private adjust(key: string, by: number): void {
    const decl = this.pack.vars.find((v) => v.name === key);
    if (!decl) {
      // The validator rejects this at build time; a pack reaching the runtime
      // without it is broken, and guessing a domain would hide that.
      console.warn(`[hon] adjust "${key}": not declared in pack.vars — ignored`);
      return;
    }
    if (decl.type !== 'int') {
      console.warn(`[hon] adjust "${key}": only int variables can be adjusted (declared ${decl.type})`);
      return;
    }
    const current = this.vars[key];
    const base = typeof current === 'number' ? current : Number(initialValue(decl));
    const [min, max] = decl.domain ?? [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER];
    this.vars[key] = Math.max(min, Math.min(max, base + by));
  }

  /**
   * Stable identity for the node currently executing, used for skip-read.
   *
   * It must include the whole frame path, not just the top frame's cursor.
   * `if` pushes a nested frame whose cursor restarts at 0, so a `say` at index 2
   * of a scene body and a `say` at index 2 inside a nested branch would otherwise
   * produce the same key — and skip-read would then skip a line the player never
   * saw, or refuse to skip one they read. Invisible in a flat scaffold pack;
   * a correctness bug the moment `if` branches contain dialogue.
   */
  private fingerprint(): string {
    const path = this.frames.map((f) => f.cursor - 1).join('.');
    return `${this.book.id}/${this.episode.id}/${this.sceneId}#${path}`;
  }

  private loadScene(id: string): void {
    // Scene ids are episode-scoped, so this never crosses an episode boundary.
    const scene = this.episode.scenes.find((s) => s.id === id);
    if (!scene) throw new Error(`[hon] unknown scene "${id}" in ${this.book.id}/${this.episode.id}`);
    this.sceneId = id;
    this.frames = [{ nodes: scene.nodes, cursor: 0 }];
    this.emit({ type: 'scene', id });
  }

  /**
   * Run until the next event that needs the player — a line of dialogue, a choice,
   * or an explicit wait. Non-blocking ops (show/bg/bgm/set/if) are drained inline.
   */
  advance(): void {
    if (this.awaitingChoice || this.awaitingInput) return;

    for (;;) {
      const frame = this.frames[this.frames.length - 1];
      if (!frame) { this.emit(this.endEvent()); return; }

      if (frame.cursor >= frame.nodes.length) {
        this.frames.pop();
        if (this.frames.length === 0) { this.emit(this.endEvent()); return; }
        continue;
      }

      const node = frame.nodes[frame.cursor]!;
      frame.cursor++;

      switch (node.op) {
        case 'say': {
          const ch = node.who ? this.pack.characters.find((c) => c.id === node.who) : undefined;
          /*
           * The plate shows what the player called her. `nameVar` is only set
           * on a character the story lets you rename, and falls back the moment
           * the variable is empty — which is every pack that never asks.
           */
          const chosen = ch?.nameVar ? this.vars[ch.nameVar] : undefined;
          const shown = typeof chosen === 'string' && chosen ? chosen : ch?.name;
          this.seen.add(this.fingerprint());
          this.history.push({ who: node.who, name: shown, text: node.text });
          this.emit({
            type: 'say',
            who: node.who,
            name: shown,
            color: ch?.color ?? '#ffffff',
            text: node.text,
            ...(node.emote !== undefined ? { emote: node.emote } : {}),
            ...(node.voice !== undefined ? { voice: node.voice } : {}),
            ...(node.at !== undefined ? { at: node.at } : {}),
          });
          return;
        }
        case 'choice': {
          const options = node.options
            .map((o, index) => ({ ...o, index }))
            .filter((o) => !o.if || evaluate(o.if, this.scope))
            .map(({ text, goto, index, premium, cost }) => ({
              text, goto, index, premium, diamonds: cost?.diamonds ?? 0,
            }));

          /*
           * Every option's condition came back false, so there is nothing to
           * render and nothing to click — and blocking on a choice the player
           * cannot answer hangs the game permanently.
           *
           * `packages/validate` makes this unshippable by requiring one
           * unconditional option per choice; this is the second layer, for a
           * pack that reached the runtime some other way (editor preview, a
           * hand-edited draft). Falling through is the safe response: the
           * cursor is already past the choice, so the rest of the scene plays.
           * Auto-picking the first option would be worse — it could commit the
           * player to a branch, and one day a paid one.
           */
          if (options.length === 0) {
            console.error(`[hon] choice at ${this.sceneId} filtered every option away; falling through`);
            break;
          }

          this.awaitingChoice = true;
          this.emit({
            type: 'choice',
            ...(node.prompt !== undefined ? { prompt: node.prompt } : {}),
            ...(node.promptImage !== undefined ? { promptImage: node.promptImage } : {}),
            options,
          });
          return;
        }
        case 'card':
          /* Holds like a line does: `advance()` is what dismisses it. */
          this.emit({
            type: 'card',
            ...(node.title !== undefined ? { title: node.title } : {}),
            ...(node.subtitle !== undefined ? { subtitle: node.subtitle } : {}),
          });
          return;

        case 'input': {
          /*
           * Suspends like a choice does. Seeded with whatever the variable
           * already holds so re-entering the screen shows the current name
           * rather than resetting it to the default.
           */
          const held = this.vars[node.key];
          const value = typeof held === 'string' && held ? held : node.default;
          this.awaitingInput = { key: node.key, fallback: node.default };
          this.emit({
            type: 'input',
            key: node.key,
            prompt: node.prompt,
            ...(node.note !== undefined ? { note: node.note } : {}),
            value,
            maxLength: node.maxLength,
          });
          return;
        }
        case 'wait':
          this.emit({ type: 'wait', ms: node.ms });
          return;

        case 'show':
          this.emit({
            type: 'show',
            sprite: node.sprite,
            at: node.at,
            ...(node.emote !== undefined ? { emote: node.emote } : {}),
            ...(node.frame !== undefined ? { frame: node.frame } : {}),
            transition: node.with ?? DEFAULT_TRANSITION,
          });
          break;
        case 'hide':
          this.emit({ type: 'hide', sprite: node.sprite, transition: node.with ?? DEFAULT_TRANSITION });
          break;
        case 'bg':
          this.emit({
            type: 'bg',
            image: node.image,
            ...(node.frame !== undefined ? { frame: node.frame } : {}),
            transition: node.with ?? DEFAULT_TRANSITION,
          });
          break;
          break;
        case 'bgm':
          this.emit({ type: 'bgm', track: node.track, fadeMs: node.fadeMs });
          break;
        case 'sfx':
          this.emit({ type: 'sfx', clip: node.clip, volume: node.volume });
          break;
        case 'fx':
          this.emit({ type: 'fx', effect: node.effect, ms: node.ms, intensity: node.intensity });
          break;
        case 'set':
          this.vars[node.key] = node.value;
          break;
        case 'adjust':
          this.adjust(node.key, node.by);
          break;
        case 'if': {
          const branch = evaluate(node.cond, this.scope) ? node.then : node.else;
          if (branch?.length) this.frames.push({ nodes: branch, cursor: 0 });
          break;
        }
        case 'jump':
          this.loadScene(node.scene);
          break;
      }
    }
  }

  /**
   * Resolve a pending `input` with what the player typed.
   *
   * An empty answer is the default, not an empty name — the storyboard's field
   * starts filled with «МАРИНА» and clearing it is not a way to have no name.
   */
  submit(value: string): void {
    if (!this.awaitingInput) return;
    const { key, fallback } = this.awaitingInput;
    this.awaitingInput = null;
    this.vars[key] = value.trim() || fallback;
    this.advance();
  }

  /** Resolve a pending choice by its original index. */
  choose(index: number): void {
    if (!this.awaitingChoice) return;
    const frame = this.frames[this.frames.length - 1];
    const node = frame?.nodes[frame.cursor - 1];
    if (node?.op !== 'choice') return;

    const option = node.options[index];
    if (!option) return;

    this.recordChoice(node, index);

    this.awaitingChoice = false;
    this.awaitingInput = null;
    this.loadScene(option.goto);
    this.advance();
  }

  /**
   * Append to the recap log.
   *
   * The record keys on `Choice.id`, never on where the node happens to sit:
   * inserting a line above a choice shifts every index below it, which would
   * silently re-point historical records — survivable for a recap, a
   * money bug for a paid-unlock ledger. Where the author has not set an id
   * there is nothing stable to use, so the positional path is stored with a
   * leading `#` to mark it as exactly that: a fallback a reader must not trust
   * across pack versions.
   */
  private recordChoice(node: Extract<Node, { op: 'choice' }>, index: number): void {
    // Far above the ~120-choice season budget; this is a runaway guard, not a
    // policy. Oldest goes first so the current stretch of play stays intact.
    if (this.choices.length >= MAX_CHOICE_RECORDS) this.choices.shift();

    this.choices.push({
      e: this.episode.id,
      s: this.sceneId,
      c: node.id ?? `#${this.frames.map((f) => f.cursor - 1).join('.')}`,
      o: index,
      t: this.clock(),
    });
  }

  /* ── persistence ──────────────────────────────────────────────────────── */

  save(now: number): SaveState {
    /*
     * Once an episode ends every frame has been popped, so the live stack is
     * empty — which is not a position a save can express. Represent "finished"
     * as a cursor one past the end of the current scene: restoring it runs out
     * immediately and re-emits `end`, which is exactly what happened.
     */
    const scene = this.episode.scenes.find((s) => s.id === this.sceneId);
    const stack = this.frames.length > 0
      ? this.frames.map((f) => f.cursor)
      : [scene?.nodes.length ?? 0];

    return {
      version: 3,
      packId: this.pack.id,
      book: this.book.id,
      episode: this.episode.id,
      scene: this.sceneId,
      // The whole stack, not just the top cursor — see SaveState's docstring.
      stack,
      completed: [...this.completed],
      vars: { ...this.vars },
      seen: [...this.seen],
      choices: [...this.choices],
      savedAt: now,
      playtimeMs: Math.max(0, now - this.startedAt),
      affection: {},
      stats: {},
      look: {},
      outfit: {},
    };
  }

  restore(state: SaveState, now: number): void {
    if (state.packId !== this.pack.id) {
      throw new Error(`[hon] save is for pack ${state.packId}, loaded pack is ${this.pack.id}`);
    }
    const target = findEpisode(this.pack, state.book, state.episode);
    if (!target) {
      throw new Error(`[hon] save points at ${state.book}/${state.episode}, which this pack does not contain`);
    }
    this.book = target.book;
    this.episode = target.episode;
    this.completed = new Set(state.completed);

    // Flags first: rebuilding the frame stack re-evaluates `if` conditions, and
    // those must see the same values they saw when the save was written.
    this.vars = { ...state.vars };
    this.seedVars();   // a save written before a variable existed has no value for it
    this.seen = new Set(state.seen);
    this.choices = [...state.choices];

    this.loadScene(state.scene);
    this.rebuildStack(state.stack, state.scene);

    this.awaitingChoice = false;
    this.awaitingInput = null;
    this.history.length = 0;
    this.startedAt = now - state.playtimeMs;
  }

  /**
   * Walk back down into nested `if` branches to the saved position.
   *
   * Each level below the first was entered through an `if` at `cursor - 1` in
   * the level above. Re-evaluating that condition against the restored flags is
   * deterministic, so it selects the same branch the player was actually in.
   *
   * If the pack has changed under an old save — the node is no longer an `if`,
   * or the condition now goes the other way — the position is unrecoverable, so
   * fall back to the start of the scene rather than resuming somewhere arbitrary.
   */
  private rebuildStack(stack: number[], sceneId: string): void {
    const top = this.frames[0];
    if (!top) return;

    for (let level = 0; level < stack.length - 1; level++) {
      const cursor = stack[level]!;
      const frame = this.frames[level];
      if (!frame) break;
      frame.cursor = cursor;

      const node = frame.nodes[cursor - 1];
      if (node?.op !== 'if') {
        console.warn(`[hon] save points inside a branch that no longer exists in ${sceneId}; restarting the scene`);
        this.frames = [{ nodes: top.nodes, cursor: 0 }];
        return;
      }

      const branch = evaluate(node.cond, this.scope) ? node.then : node.else;
      if (!branch?.length) {
        console.warn(`[hon] branch at ${sceneId}#${cursor - 1} is now empty; restarting the scene`);
        this.frames = [{ nodes: top.nodes, cursor: 0 }];
        return;
      }
      this.frames.push({ nodes: branch, cursor: 0 });
    }

    const last = this.frames[this.frames.length - 1];
    if (last) last.cursor = stack[stack.length - 1] ?? 0;
  }
}

